<script setup lang="ts">
import { TETopLabelLayout, TETextInput, TEButton } from '@ben/vite-te'
import '@iframe-resizer/child'
import { Head } from '@inertiajs/vue3'
import axios from 'axios'
import MarkdownIt from 'markdown-it'

import type { Campaign } from '@/js/Types/laravel'

const props = defineProps<{
  campaign: Campaign
  participations: number
}>()
const markdown = new MarkdownIt()
const hasSigned = ref<boolean>(false)

function sign() {
  form.clearErrors()
  axios
    .post(route('campaign.participate', { campaign: props.campaign.id }), form)
    .then(() => {
      hasSigned.value = true
    })
    .catch((error) => {
      console.error(error)
      if (error.response && error.response.status === 422) {
        form.errors = error.response.data.errors
        form.errors.first_name = error.response.data.errors.first_name?.[0]
        form.errors.last_name = error.response.data.errors.last_name?.[0]
        form.errors.email = error.response.data.errors.email?.[0]
        form.errors.zip = error.response.data.errors.zip?.[0]
      }
    })
}

const fullDescription = ref<boolean>(false)

function showFullDescription() {
  fullDescription.value = !fullDescription.value
}

const form = useForm<{
  first_name: string
  last_name: string
  email: string
  zip: string
  newsletter: boolean
}>({
  first_name: '',
  last_name: '',
  email: '',
  zip: '',
  newsletter: false,
})
</script>

<template>
  <div class="mx-4 mt-4 max-w-5xl px-2 sm:mx-auto sm:px-0">
    <Head :title="campaign.name" />

    <main class="-mx-4 mb-4 sm:mx-4">
      <div class="box mt-4 flex flex-wrap rounded-3xl px-6 py-4">
        <div class="w-full">
          <h1 class="bg-[#1b7340] px-2 text-[2.25rem] font-extrabold text-white">
            {{ campaign.name }}
          </h1>
        </div>

        <Counter :current="participations" :goal="campaign.goal" auto-refresh />

        <div class="w-full">
          <p class="text-lg font-semibold">{{ campaign.recipients }},</p>

          <TransitionGroup
            tag="div"
            enter-active-class="transition-opacity duration-500 ease-out"
            leave-active-class="transition-opacity duration-500 ease-in"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <p v-if="!fullDescription" class="mt-2">
              {{ campaign.short_description }}
              <span
                class="cursor-pointer font-semibold text-primary-500"
                @click="showFullDescription"
                >Mehr Lesen</span
              >
            </p>

            <div
              v-if="fullDescription"
              class="mt-2"
              v-html="markdown.render(props.campaign.description)"
            />
          </TransitionGroup>
        </div>
      </div>

      <TransitionGroup
        name="list"
        tag="div"
        enter-active-class="transition transform duration-500 ease-out"
        leave-active-class="transition transform duration-500 ease-in"
        enter-from-class="translate-x-full opacity-0"
        enter-to-class="translate-x-0 opacity-100"
        leave-from-class="translate-x-0 opacity-100"
        leave-to-class="-translate-x-full opacity-0"
      >
        <div v-if="!hasSigned" class="box mt-4 flex flex-wrap rounded-3xl px-6 py-4">
          <div class="w-full bg-[#1b7340] px-2 text-[2.25rem] font-extrabold text-white">
            Jetzt unterschreiben
          </div>

          <form class="w-full" @submit.prevent>
            <div class="w-full gap-4 sm:grid sm:grid-cols-2">
              <TETopLabelLayout
                title="Vorname"
                :error="form.errors.first_name"
                name="first_name"
                required
              >
                <TETextInput v-model="form.first_name" name="first_name" />
              </TETopLabelLayout>

              <TETopLabelLayout
                title="Nachname"
                name="last_name"
                :error="form.errors.last_name"
                required
              >
                <TETextInput v-model="form.last_name" name="last_name" autocomplete="given-name" />
              </TETopLabelLayout>

              <TETopLabelLayout title="Email" name="email" :error="form.errors.email" required>
                <TETextInput v-model="form.email" name="email" autocomplete="email" />
              </TETopLabelLayout>

              <TETopLabelLayout title="Postleitzahl" name="zip" required :error="form.errors.zip">
                <TETextInput
                  v-model="form.zip"
                  type="number"
                  autocomplete="postal-code"
                  maxlength="5"
                  minlength="5"
                  name="zip"
                />
              </TETopLabelLayout>

              <div class="relative col-span-2 mb-2 flex items-start">
                <div class="flex h-6 items-center">
                  <input
                    id="newsletter"
                    v-model="form.newsletter"
                    aria-describedby="comments-description"
                    name="comments"
                    type="checkbox"
                    class="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                  />
                </div>

                <div class="ml-3 text-sm leading-6">
                  <label for="newsletter" class="font-medium text-gray-900"
                    >Ich will über Neues zur Kampagne sowie zu Fridays for Future informiert
                    werden.</label
                  >
                </div>
              </div>
            </div>

            <div class="col-span-2 mb-2">
              <span class="text-sm text-gray-500"
                >Ich bin damit einverstanden, dass die Unterzeichnung dieser Petition sowie meine
                E-Mail-Adresse, Name und Ort zur Durchführung der Petition bis zu deren Ende
                gespeichert werden. Mein Vorname, der erste Buchstabe meines Nachnamens und mein
                Wohnort werden den Empfänger*in der Petition überreicht. Zur Verifizierung meiner
                Daten erhalte ich eine entsprechende E-Mail mit weiteren Informationen. Diese
                Einwilligung kann jederzeit widerrufen werden.
                <a href="https://fridaysforfuture.de/datenschutz" class="underline"
                  >Mehr Infos</a
                ></span
              >
            </div>

            <TEButton id="sign" type="button" class="w-full !rounded-full" primary @click="sign"
              ><span class="-m-2 text-xl font-bold uppercase">Jetzt Unterschreiben</span>
            </TEButton>
          </form>
        </div>

        <div v-else class="box mt-4 flex flex-wrap rounded-3xl px-6 py-4">
          <div class="w-full bg-[#1b7340] px-2 text-[2.25rem] font-extrabold text-white">
            Noch ein Schritt fehlt - jetzt E-Mail bestätigen!
          </div>

          <div>
            <p class="mt-4">
              Rufe deine E-Mail jetzt ab und klicke auf den Bestätigungslink. Nur dann können wir
              deine Stimme zählen!
            </p>

            <p>
              Die Bestätigung stellt sicher, dass jede Unterschrift tatsächlich von einem Menschen
              kommt.
            </p>

            <p class="whitespace-pre-wrap">{{ campaign.email_footer }}</p>
          </div>
        </div>
      </TransitionGroup>
    </main>
  </div>
</template>

<style>
.box {
  box-shadow: 1px 3px 5px 0 rgba(0, 0, 0, 0.3);
}

li {
  list-style-type: disc;
  margin-left: 2rem;
}

p + p {
  margin-top: 1rem;
}

body {
  @apply !bg-white;
}
</style>
